import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../lib/auth';
import { fileKeys } from '../lib/files';
import { LIMITS_KEY } from '../lib/limits';
import { searchKeys } from '../lib/search';
import { type MessageRole, type MessageRow, sessionKeys, useMessages } from '../lib/sessions';
import { type Effort, type Model, type PermissionMode, isModel } from '../lib/settings';
import {
  ChatSocket,
  type ConnectionState,
  type ContentBlock,
  type RateLimitInfo,
  type ServerEvent,
  type ToolCall,
  type UsageInfo,
} from '../lib/ws';

/**
 * The chat state machine for a single session:
 *   - hydrates persisted history via REST (useMessages)
 *   - opens a WebSocket and subscribes by sessionId so the server
 *     can replay any run that's still running (closed-tab scenario)
 *   - while streaming, owns the in-flight assistant message — text,
 *     thinking and tool-calls — so the UI can show incremental
 *     progress
 *   - on `done`, invalidates the messages query so the persisted
 *     list wins
 */

export type DisplayMessage = {
  id: string;
  role: MessageRole;
  /** Undefined for legacy user rows without persisted attachment metadata. */
  attachments?: string[];
  /**
   * Ordered list of content blocks for this turn. For user/system
   * rows it's a single text block. For assistant rows it preserves
   * the original text/tool/text… interleaving so the UI can render
   * a tool right next to the prose that produced it (claude.ai
   * parity). Empty `[]` for an assistant row that hasn't streamed
   * any output yet.
   */
  blocks: ContentBlock[];
  streaming?: boolean;
  thinking?: string;
  /**
   * Quick lookup table for tool_use blocks by id. Same data the
   * blocks array references — keeping it as a flat list lets the
   * renderer grab a tool's `name`/`input`/`result`/`isError` in one
   * indexed lookup rather than walking the blocks every time.
   */
  toolCalls?: ToolCall[];
};

/** Concatenated text of a message — used by callers that don't
 * care about ordering (artifact extraction from fenced code blocks,
 * search highlighting, etc.). For user/system rows it's the single
 * text block; for assistant rows it joins every text block in
 * order. */
export function messageText(m: DisplayMessage): string {
  let out = '';
  for (const b of m.blocks) {
    if (b.type === 'text') out += b.text;
  }
  return out;
}

export type ChatState = {
  messages: DisplayMessage[];
  isStreaming: boolean;
  /** Local-clock ms when the current run started. `null` when
   * nothing is streaming. Survives reloads because the server's
   * snapshot brings back `elapsedMs`. */
  streamingStartedAt: number | null;
  connectionState: ConnectionState;
  lastActivityAt: number | null;
  statusMessage: string | null;
  usage: UsageInfo | null;
  rateLimit: RateLimitInfo | null;
  error: string | null;
  /** True while a `/compact` (or auto-compaction) is running this
   * turn — drives the "compacting…" composer indicator. */
  compacting: boolean;
  /** Set when a compaction failed; a transient inline notice reads
   * it. Cleared on the next send. */
  compactError: string | null;
  /** Set when the server reports a safety-refusal model swap mid-turn
   * (a Fable turn the API refused, retried on Opus). The composer's
   * model picker reads it to latch onto `toModel` so the *next* turn
   * continues on Opus instead of snapping back to Fable and tripping
   * the same filter. `null` when there's nothing to consume. */
  modelSwitch: Model | null;
  /** Acknowledge the `modelSwitch` signal once the picker has applied
   * it (mirrors the composer's `externalFiles` drain). */
  clearModelSwitch: () => void;
  sendMessage: (
    text: string,
    model?: Model,
    permission?: PermissionMode,
    attachments?: string[],
    effort?: Effort,
  ) => Promise<void>;
  /** Rewrite a sent user message and regenerate the assistant turn. */
  editResend: (
    messageId: string,
    text: string,
    model?: Model,
    permission?: PermissionMode,
    attachments?: string[],
    effort?: Effort,
  ) => Promise<void>;
  abort: () => void;
};

function upsertToolCall(
  existing: ToolCall[] | undefined,
  id: string,
  patch: Partial<ToolCall> & { name?: string; input?: unknown },
): ToolCall[] {
  const base = existing ?? [];
  const idx = base.findIndex((t) => t.id === id);
  if (idx === -1) {
    return [...base, { id, name: patch.name ?? '', input: patch.input, ...patch }];
  }
  const next = base.slice();
  // Only overwrite fields that the patch actually defines — otherwise
  // `tool_use_end` would wipe the name/input set by `tool_use_start`.
  const merged: ToolCall = { ...base[idx] };
  if (patch.name !== undefined && patch.name !== '') merged.name = patch.name;
  if (patch.input !== undefined) merged.input = patch.input;
  if (patch.result !== undefined) merged.result = patch.result;
  if (patch.isError !== undefined) merged.isError = patch.isError;
  next[idx] = merged;
  return next;
}

/**
 * Apply a streaming text-delta to the blocks array. Returns a new
 * array (immutable update) so React notices the change. If
 * `blockIndex` falls past the current length the gap is padded
 * with empty text blocks; if it lands on a tool_use block the
 * delta is dropped (server bug — text_delta should never target a
 * tool slot).
 */
function applyTextDelta(blocks: ContentBlock[], blockIndex: number, text: string): ContentBlock[] {
  if (text.length === 0) return blocks;
  const next = blocks.slice();
  while (next.length <= blockIndex) {
    next.push({ type: 'text', text: '' });
  }
  const slot = next[blockIndex];
  if (slot.type === 'text') {
    next[blockIndex] = { type: 'text', text: slot.text + text };
  }
  return next;
}

/**
 * Slot a tool_use block at `blockIndex`. Pads the array with empty
 * text placeholders if the index is past the tail. Idempotent: if
 * the same tool already occupies that slot we leave it alone, and
 * a slot containing different content is replaced.
 */
function applyToolUseStart(
  blocks: ContentBlock[],
  blockIndex: number,
  toolCallId: string,
): ContentBlock[] {
  const next = blocks.slice();
  while (next.length <= blockIndex) {
    next.push({ type: 'text', text: '' });
  }
  const existing = next[blockIndex];
  if (existing.type === 'tool_use' && existing.toolCallId === toolCallId) {
    return blocks;
  }
  // The server never targets a compact slot with a tool, but never
  // clobber a divider if an out-of-order frame ever lands here.
  if (existing.type === 'compact') {
    return blocks;
  }
  next[blockIndex] = { type: 'tool_use', toolCallId };
  return next;
}

/**
 * Slot a compact-boundary block at `blockIndex` (pads the tail with
 * empty text placeholders if needed). Mirrors `applyToolUseStart`;
 * returns a new array for React.
 */
function applyCompactBoundary(
  blocks: ContentBlock[],
  blockIndex: number,
  trigger: 'auto' | 'manual',
  preTokens?: number,
  postTokens?: number,
): ContentBlock[] {
  const next = blocks.slice();
  while (next.length <= blockIndex) {
    next.push({ type: 'text', text: '' });
  }
  next[blockIndex] = { type: 'compact', trigger, preTokens, postTokens };
  return next;
}

/**
 * Slot a model-fallback divider at `blockIndex` (pads the tail with
 * empty text placeholders if needed). Mirrors `applyCompactBoundary`;
 * returns a new array for React.
 */
function applyModelFallback(
  blocks: ContentBlock[],
  blockIndex: number,
  fromModel: string,
  toModel: string,
): ContentBlock[] {
  const next = blocks.slice();
  while (next.length <= blockIndex) {
    next.push({ type: 'text', text: '' });
  }
  next[blockIndex] = { type: 'model_fallback', fromModel, toModel };
  return next;
}

/** Last text block index, or -1 if none. */
function lastTextIndex(blocks: ContentBlock[]): number {
  for (let i = blocks.length - 1; i >= 0; i -= 1) {
    if (blocks[i].type === 'text') return i;
  }
  return -1;
}

/**
 * For pre-fix servers (or pre-migration history rows) we get a
 * single `assistantText` and a flat `toolCalls` list — order is
 * lost. Synthesise something reasonable: the entire text first,
 * then the tools. Matches the old behaviour, which means at worst
 * we render the same as before the fix instead of crashing.
 */
function fallbackBlocks(text: string, toolCalls: ToolCall[]): ContentBlock[] {
  const out: ContentBlock[] = [];
  if (text.length > 0) out.push({ type: 'text', text });
  for (const tc of toolCalls) {
    out.push({ type: 'tool_use', toolCallId: tc.id });
  }
  return out;
}

export function useChat(sessionId: string | null): ChatState {
  const qc = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;
  const {
    data: historyRaw,
    error: historyQueryError,
    isError: historyFailed,
  } = useMessages(sessionId);
  const historyLoadError = historyFailed
    ? historyQueryError instanceof Error
      ? `Не удалось загрузить историю сообщений: ${historyQueryError.message}`
      : 'Не удалось загрузить историю сообщений'
    : null;
  const socketRef = useRef<ChatSocket | null>(null);

  const [streamingMsg, setStreamingMsg] = useState<DisplayMessage | null>(null);
  const [echoUserMsg, setEchoUserMsg] = useState<DisplayMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingStartedAt, setStreamingStartedAt] = useState<number | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [lastActivityAt, setLastActivityAt] = useState<number | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [rateLimit, setRateLimit] = useState<RateLimitInfo | null>(null);
  // Transient compaction state for a `/compact` (or auto-compaction)
  // turn: `compacting` drives the "compacting…" indicator, `compactError`
  // a clean inline failure notice. Neither is persisted — both reset on
  // the next send / done / error.
  const [compacting, setCompacting] = useState(false);
  const [compactError, setCompactError] = useState<string | null>(null);
  // Transient one-shot signal: the server saw a safety-refusal model
  // swap this turn, so the composer should latch its picker onto the
  // fallback model for subsequent turns. Consumed (cleared) by the
  // composer right after it applies — same drain pattern as the
  // external-file attachments handoff.
  const [modelSwitch, setModelSwitch] = useState<Model | null>(null);
  // idle → pending (local send lock) → live (assistant_start / snapshot).
  // Composer reads isStreaming; the ref is the sync guard against a
  // second Enter before React re-renders.
  const streamPhaseRef = useRef<'idle' | 'pending' | 'live'>('idle');
  const runEpochRef = useRef(0);
  const activeMessageIdRef = useRef<string | null>(null);
  const overlaySessionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!sessionId || userId == null) return;
    const sock = new ChatSocket();
    socketRef.current = sock;
    overlaySessionRef.current = sessionId;
    let disposed = false;
    const messagesKey = sessionKeys.messageList(sessionId, userId);
    const markActivity = (messageId: string): boolean => {
      if (activeMessageIdRef.current !== messageId) return false;
      setLastActivityAt(Date.now());
      return true;
    };

    const refreshHistory = (settle = false) => {
      const epoch = runEpochRef.current;
      void qc.invalidateQueries({ queryKey: sessionKeys.list(userId), exact: true });
      void qc
        // Even the initial load can predate `done`; invalidate alone
        // reuses that request when the cache does not have data yet.
        .cancelQueries({ queryKey: messagesKey, exact: true })
        .then(() => {
          if (disposed || epoch !== runEpochRef.current) return;
          return qc.invalidateQueries(
            { queryKey: messagesKey, exact: true },
            { throwOnError: true },
          );
        })
        .then(() => {
          if (disposed || epoch !== runEpochRef.current) return;
          const query = qc.getQueryState(messagesKey);
          if (query?.status !== 'success' || query.fetchStatus !== 'idle' || query.isInvalidated) {
            return;
          }
          const rows = qc.getQueryData<MessageRow[]>(messagesKey);
          const persisted = rows?.some((row) => row.id === activeMessageIdRef.current);
          if (!rows || (!settle && !persisted)) return;
          // Keep the live copy until REST succeeds. A late refresh must
          // never clear a newer turn or leave a missed `done` spinning.
          activeMessageIdRef.current = null;
          streamPhaseRef.current = 'idle';
          setIsStreaming(false);
          setStreamingStartedAt(null);
          setCompacting(false);
          setLastActivityAt(null);
          setStatusMessage(null);
          setStreamingMsg(null);
          setEchoUserMsg(null);
        })
        .catch(() => {
          // useMessages exposes the error; retain the last visible reply.
        });
    };

    const off = sock.on((ev: ServerEvent) => {
      switch (ev.kind) {
        case 'user_message':
          setLastActivityAt(Date.now());
          setEchoUserMsg({
            id: ev.messageId,
            role: 'user',
            blocks: [{ type: 'text', text: ev.text }],
            attachments: ev.attachments,
          });
          // Edit-resend rewrites an existing id; refresh history so
          // truncated assistant turns disappear from the transcript.
          void qc.invalidateQueries({ queryKey: messagesKey, exact: true });
          break;
        case 'assistant_start':
          runEpochRef.current += 1;
          activeMessageIdRef.current = ev.messageId;
          streamPhaseRef.current = 'live';
          setError(null);
          setLastActivityAt(Date.now());
          setStatusMessage(null);
          setCompacting(false);
          setCompactError(null);
          setStreamingMsg({
            id: ev.messageId,
            role: 'assistant',
            blocks: [],
            streaming: true,
            thinking: '',
            toolCalls: [],
          });
          setIsStreaming(true);
          // Reconstruct server start time in local clock so the
          // heartbeat timer is aligned on both sides.
          setStreamingStartedAt(typeof ev.startedAtMs === 'number' ? ev.startedAtMs : Date.now());
          break;
        case 'snapshot': {
          runEpochRef.current += 1;
          const sameRun = activeMessageIdRef.current === ev.messageId;
          if (!sameRun) setEchoUserMsg(null);
          activeMessageIdRef.current = ev.messageId;
          // Server is replaying an in-flight run (or a just-finished
          // one, if we reconnected seconds after `done`). Seed the
          // streaming message with whatever it accumulated so far.
          // Prefer ordered `contentBlocks` (post-fix server). Fall
          // back to synthesising from the flat projection if the
          // server is older than this client.
          const seedBlocks =
            ev.contentBlocks && ev.contentBlocks.length > 0
              ? ev.contentBlocks.map((b) => ({ ...b }))
              : fallbackBlocks(ev.assistantText, ev.toolCalls);
          setStreamingMsg({
            id: ev.messageId,
            role: 'assistant',
            blocks: seedBlocks,
            streaming: ev.isStreaming,
            thinking: ev.thinkingText,
            toolCalls: ev.toolCalls,
          });
          streamPhaseRef.current = ev.isStreaming ? 'live' : 'idle';
          setIsStreaming(ev.isStreaming);
          setStreamingStartedAt(ev.isStreaming ? Date.now() - ev.elapsedMs : null);
          setLastActivityAt((previous) =>
            typeof ev.activityElapsedMs === 'number' &&
            Number.isFinite(ev.activityElapsedMs) &&
            ev.activityElapsedMs >= 0
              ? Date.now() - ev.activityElapsedMs
              : sameRun
                ? previous
                : Date.now(),
          );
          if (ev.compacting !== undefined) setCompacting(ev.compacting);
          else if (!sameRun) setCompacting(false);
          if (ev.compactError !== undefined) setCompactError(ev.compactError);
          else if (!sameRun) setCompactError(null);
          if (ev.statusMessage !== undefined) setStatusMessage(ev.statusMessage);
          else if (!sameRun) setStatusMessage(null);
          if (ev.error !== undefined) setError(ev.error);
          else if (!sameRun) setError(null);
          if (ev.usage !== undefined) setUsage(ev.usage);
          if (ev.rateLimit !== undefined) setRateLimit(ev.rateLimit);
          refreshHistory(!ev.isStreaming);
          break;
        }
        case 'idle':
          runEpochRef.current += 1;
          streamPhaseRef.current = 'idle';
          setIsStreaming(false);
          setStreamingStartedAt(null);
          setCompacting(false);
          setLastActivityAt(null);
          setStatusMessage(null);
          setUsage(null);
          setStreamingMsg((prev) => (prev ? { ...prev, streaming: false } : prev));
          refreshHistory(true);
          break;
        case 'activity':
          if (!markActivity(ev.messageId)) break;
          setStatusMessage(ev.message.trim() || null);
          break;
        case 'usage':
          setUsage(ev.usage);
          if (streamPhaseRef.current === 'live') setLastActivityAt(Date.now());
          break;
        case 'rate_limit':
          setRateLimit(ev.rateLimit);
          // Refresh the global /api/limits so other open chats and
          // the header badge pick up the new numbers immediately,
          // not after the 15-s polling tick.
          qc.invalidateQueries({ queryKey: LIMITS_KEY });
          break;
        case 'thinking_delta':
          if (markActivity(ev.messageId)) setStatusMessage(null);
          setStreamingMsg((prev) =>
            prev && prev.id === ev.messageId
              ? { ...prev, thinking: (prev.thinking ?? '') + ev.text }
              : prev,
          );
          break;
        case 'assistant_delta':
          if (markActivity(ev.messageId)) setStatusMessage(null);
          setStreamingMsg((prev) => {
            if (!prev || prev.id !== ev.messageId) return prev;
            // Pre-fix servers don't send blockIndex — append to a
            // tail text block (matches old "all text at the end"
            // behaviour, which is the worst case).
            const idx =
              typeof ev.blockIndex === 'number'
                ? ev.blockIndex
                : Math.max(0, lastTextIndex(prev.blocks));
            return { ...prev, blocks: applyTextDelta(prev.blocks, idx, ev.text) };
          });
          break;
        case 'tool_use_start':
          if (markActivity(ev.messageId)) setStatusMessage(null);
          setStreamingMsg((prev) => {
            if (!prev || prev.id !== ev.messageId) return prev;
            const toolCalls = upsertToolCall(prev.toolCalls, ev.toolUseId, {
              name: ev.name,
              input: ev.input,
            });
            // No blockIndex from older servers → tail-append the
            // tool slot, which keeps it after any text we've
            // received so far.
            const idx = typeof ev.blockIndex === 'number' ? ev.blockIndex : prev.blocks.length;
            const blocks = applyToolUseStart(prev.blocks, idx, ev.toolUseId);
            return { ...prev, toolCalls, blocks };
          });
          break;
        case 'tool_use_end':
          if (markActivity(ev.messageId)) setStatusMessage(null);
          // No block changes — `tool_use_end` only fills `result`
          // / `isError` on the existing toolCall entry, which the
          // renderer reads through the `toolCallId` reference.
          setStreamingMsg((prev) =>
            prev && prev.id === ev.messageId
              ? {
                  ...prev,
                  toolCalls: upsertToolCall(prev.toolCalls, ev.toolUseId, {
                    result: ev.result,
                    isError: ev.isError,
                  }),
                }
              : prev,
          );
          break;
        case 'compact_boundary':
          if (!markActivity(ev.messageId)) break;
          // A boundary only arrives on success, after `compact_status`
          // done — defensively clear the indicator in case the done
          // frame was dropped.
          setCompacting(false);
          setStatusMessage(null);
          setStreamingMsg((prev) => {
            if (!prev || prev.id !== ev.messageId) return prev;
            return {
              ...prev,
              blocks: applyCompactBoundary(
                prev.blocks,
                ev.blockIndex,
                ev.trigger,
                ev.preTokens,
                ev.postTokens,
              ),
            };
          });
          break;
        case 'compact_status':
          if (!markActivity(ev.messageId)) break;
          if (ev.phase === 'start') {
            setCompacting(true);
            setCompactError(null);
            setStatusMessage(null);
          } else if (ev.phase === 'done') {
            setCompacting(false);
            setStatusMessage(null);
          } else {
            // failed
            setCompacting(false);
            setCompactError(ev.error ?? 'Не удалось сжать контекст');
            setStatusMessage(null);
          }
          break;
        case 'model_fallback':
          if (markActivity(ev.messageId)) setStatusMessage(null);
          // The API refused this Fable turn and the CLI retried on Opus.
          // (1) Drop a positional divider into the transcript so the
          // swap is visible; (2) latch the composer onto `toModel` so
          // the next turn starts on Opus instead of re-tripping the
          // filter. Ignore an unknown alias rather than poison the
          // picker.
          setStreamingMsg((prev) => {
            if (!prev || prev.id !== ev.messageId) return prev;
            return {
              ...prev,
              blocks: applyModelFallback(prev.blocks, ev.blockIndex, ev.fromModel, ev.toModel),
            };
          });
          if (isModel(ev.toModel)) setModelSwitch(ev.toModel);
          break;
        case 'assistant_end':
          markActivity(ev.messageId);
          setStatusMessage(null);
          setStreamingMsg((prev) =>
            prev && prev.id === ev.messageId ? { ...prev, streaming: false } : prev,
          );
          break;
        case 'done':
          streamPhaseRef.current = 'idle';
          setIsStreaming(false);
          setStreamingStartedAt(null);
          setStreamingMsg((prev) => (prev ? { ...prev, streaming: false } : prev));
          setCompacting(false);
          setLastActivityAt(null);
          setStatusMessage(null);
          if (ev.usage !== undefined) setUsage(ev.usage);
          if (ev.rateLimit !== undefined) setRateLimit(ev.rateLimit);
          refreshHistory(true);
          void qc.invalidateQueries({ queryKey: searchKeys.user(userId) });
          void qc.invalidateQueries({ queryKey: fileKeys.user(userId) });
          qc.invalidateQueries({ queryKey: LIMITS_KEY });
          break;
        case 'error':
          setError(ev.message);
          // A rejected extra send must not tear down the live run.
          if (ev.message === 'another run in progress' && streamPhaseRef.current === 'live') {
            break;
          }
          runEpochRef.current += 1;
          streamPhaseRef.current = 'idle';
          setIsStreaming(false);
          setStreamingStartedAt(null);
          setCompacting(false);
          setLastActivityAt(null);
          setStatusMessage(null);
          setStreamingMsg((prev) => (prev ? { ...prev, streaming: false } : prev));
          refreshHistory(true);
          void qc.invalidateQueries({ queryKey: searchKeys.user(userId) });
          void qc.invalidateQueries({ queryKey: fileKeys.user(userId) });
          break;
      }
    });

    const offState = sock.onState((state) => {
      setConnectionState(state);
      if (state === 'open') refreshHistory();
    });
    const resume = () => {
      if (document.visibilityState !== 'visible') return;
      refreshHistory();
      // Do not replace a connection while its local send awaits an ack.
      if (streamPhaseRef.current === 'pending') sock.send({ kind: 'subscribe', sessionId });
      else sock.resume();
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) resume();
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('pageshow', onPageShow);
    sock.connect();
    // Ask the server whether there's already a run for this session.
    // Safe to send before open — ChatSocket queues until the socket
    // is ready.
    sock.send({ kind: 'subscribe', sessionId });

    return () => {
      disposed = true;
      runEpochRef.current += 1;
      activeMessageIdRef.current = null;
      overlaySessionRef.current = null;
      off();
      offState();
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('pageshow', onPageShow);
      sock.dispose();
      socketRef.current = null;
      streamPhaseRef.current = 'idle';
      setStreamingMsg(null);
      setEchoUserMsg(null);
      setIsStreaming(false);
      setStreamingStartedAt(null);
      setConnectionState('idle');
      setLastActivityAt(null);
      setStatusMessage(null);
      setUsage(null);
      setRateLimit(null);
      setError(null);
      setCompacting(false);
      setCompactError(null);
      setModelSwitch(null);
    };
  }, [sessionId, qc, userId]);

  // Derive DisplayMessage[] from DB history in its own memo so the
  // per-row object identities stay stable across streaming ticks.
  // Without this, every keystroke rebuilds `historyMessages` and
  // React.memo on MessageRow is defeated for the entire history.
  const historyMessages = useMemo<DisplayMessage[]>(
    () => (historyRaw ?? []).map(toDisplay),
    [historyRaw],
  );
  const messages = useMemo<DisplayMessage[]>(() => {
    // Route params change before effect cleanup resets the old live copy.
    if (overlaySessionRef.current !== sessionId) return historyMessages;
    // Overlay echoUserMsg onto history by id (edit-resend rewrites
    // an existing user row without creating a new id).
    let merged: DisplayMessage[] = historyMessages.map((m) =>
      echoUserMsg && m.id === echoUserMsg.id
        ? { ...echoUserMsg, attachments: echoUserMsg.attachments ?? m.attachments }
        : m,
    );
    if (echoUserMsg && !merged.some((m) => m.id === echoUserMsg.id)) {
      merged.push(echoUserMsg);
    }
    // Edit-resend truncates server-side, but the messages query can
    // lag one frame. While a replacement stream is live (or we just
    // rewrote the last user bubble), drop every message that sat
    // AFTER that user turn so the old assistant answer is not shown
    // as the live reply next to the new stream.
    if (echoUserMsg && (streamingMsg || isStreaming)) {
      const editIdx = merged.findIndex((m) => m.id === echoUserMsg.id);
      if (editIdx >= 0) {
        merged = merged.filter((m, i) => {
          if (i <= editIdx) return true;
          // Keep only the live streaming assistant for this turn.
          return streamingMsg != null && m.id === streamingMsg.id;
        });
      }
    }
    // REST replaces the retained stream by id, without a duplicate
    // or a blank frame while the completed reply is being fetched.
    if (streamingMsg && !merged.some((m) => m.id === streamingMsg.id)) {
      merged.push(streamingMsg);
    }
    return merged;
  }, [historyMessages, echoUserMsg, streamingMsg, isStreaming, sessionId]);

  const sendMessage = useCallback(
    async (
      text: string,
      model?: Model,
      permission?: PermissionMode,
      attachments?: string[],
      effort?: Effort,
    ) => {
      const socket = socketRef.current;
      if (!sessionId || !socket) throw new Error('Чат ещё не подключён. Черновик сохранён.');
      if (streamPhaseRef.current !== 'idle')
        throw new Error('Дождитесь завершения текущего ответа.');
      const epoch = ++runEpochRef.current;
      activeMessageIdRef.current = null;
      streamPhaseRef.current = 'pending';
      setIsStreaming(true);
      setError(null);
      setCompactError(null);
      setCompacting(false);
      setLastActivityAt(Date.now());
      setStatusMessage(null);
      try {
        await socket.submit({
          kind: 'send',
          sessionId,
          text,
          model,
          permissionMode: permission,
          attachments,
          effort,
        });
      } catch (error) {
        if (socketRef.current === socket) {
          setError(error instanceof Error ? error.message : String(error));
          if (streamPhaseRef.current === 'pending' && runEpochRef.current === epoch) {
            streamPhaseRef.current = 'idle';
            setIsStreaming(false);
            setStreamingStartedAt(null);
          }
        }
        throw error;
      }
    },
    [sessionId],
  );

  const abort = useCallback(() => {
    if (!sessionId) return;
    // Pending: no run exists yet, server abort is a no-op — unlock.
    // Live: wait for server `done`; unlocking here re-opens the
    // composer while the subprocess is still running.
    if (streamPhaseRef.current === 'pending') {
      streamPhaseRef.current = 'idle';
      setIsStreaming(false);
      setStreamingStartedAt(null);
    }
    socketRef.current?.send({ kind: 'abort', sessionId });
  }, [sessionId]);

  const editResend = useCallback(
    async (
      messageId: string,
      text: string,
      model?: Model,
      permission?: PermissionMode,
      attachments?: string[],
      effort?: Effort,
    ) => {
      const socket = socketRef.current;
      if (!sessionId || !socket) throw new Error('Чат ещё не подключён. Черновик сохранён.');
      if (streamPhaseRef.current !== 'idle')
        throw new Error('Дождитесь завершения текущего ответа.');
      const epoch = ++runEpochRef.current;
      activeMessageIdRef.current = null;
      streamPhaseRef.current = 'pending';
      setError(null);
      setCompactError(null);
      setCompacting(false);
      setLastActivityAt(Date.now());
      setStatusMessage(null);
      setStreamingMsg(null);
      setIsStreaming(true);
      setStreamingStartedAt(null);
      try {
        await socket.submit({
          kind: 'edit_resend',
          sessionId,
          messageId,
          text,
          model,
          permissionMode: permission,
          attachments,
          effort,
        });
      } catch (error) {
        if (socketRef.current === socket) {
          setError(error instanceof Error ? error.message : String(error));
          if (streamPhaseRef.current === 'pending' && runEpochRef.current === epoch) {
            streamPhaseRef.current = 'idle';
            setIsStreaming(false);
            setStreamingStartedAt(null);
          }
        }
        throw error;
      }
    },
    [sessionId],
  );

  const clearModelSwitch = useCallback(() => {
    setModelSwitch(null);
  }, []);

  return {
    messages,
    isStreaming,
    streamingStartedAt,
    connectionState,
    lastActivityAt,
    statusMessage,
    usage,
    rateLimit,
    error: error ?? historyLoadError,
    compacting,
    compactError,
    modelSwitch,
    clearModelSwitch,
    sendMessage,
    editResend,
    abort,
  };
}

function toDisplay(row: MessageRow): DisplayMessage {
  const toolCalls = parseToolCalls(row.tool_calls_json);
  // Assistant rows from after migration 12 carry an ordered
  // content_blocks_json. Older assistant rows (and every user/system
  // row) fall back to a one-text-block + tool-tail layout — same
  // behaviour as the pre-fix UI, which is the best we can do without
  // re-running the model.
  const persistedBlocks = parseContentBlocks(row.content_blocks_json);
  let blocks: ContentBlock[];
  if (persistedBlocks) {
    blocks = persistedBlocks;
  } else if (row.role === 'assistant') {
    blocks = fallbackBlocks(row.content, toolCalls ?? []);
  } else {
    blocks = [{ type: 'text', text: row.content }];
  }
  return {
    id: row.id,
    role: row.role,
    blocks,
    toolCalls,
    attachments: row.role === 'user' ? parseAttachments(row.attachments_json) : undefined,
    // Restore the persisted reasoning trace (DB migration 13) so
    // scrolling back to an older turn shows the same ThinkingBlock
    // the user saw while it was streaming. `null` / empty string →
    // omit, so MessageRow's `hasThinking` check stays correct.
    thinking: row.thinking_text && row.thinking_text.length > 0 ? row.thinking_text : undefined,
  };
}

function parseAttachments(json: string | null | undefined): string[] | undefined {
  if (!json) return undefined;
  try {
    const parsed: unknown = JSON.parse(json);
    return Array.isArray(parsed) && parsed.every((path) => typeof path === 'string')
      ? parsed
      : undefined;
  } catch {
    return undefined;
  }
}

function parseToolCalls(json: string | null): ToolCall[] | undefined {
  if (!json) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return undefined;
  }
  if (!Array.isArray(parsed)) return undefined;
  // Spot-check each entry so one bad row (old schema, truncated
  // persist) doesn't blow up the ToolCallBadge renderer.
  const out: ToolCall[] = [];
  for (const raw of parsed) {
    if (!raw || typeof raw !== 'object') continue;
    const o = raw as Partial<ToolCall>;
    if (typeof o.id !== 'string' || typeof o.name !== 'string') continue;
    out.push({
      id: o.id,
      name: o.name,
      input: o.input,
      result: typeof o.result === 'string' ? o.result : undefined,
      isError: typeof o.isError === 'boolean' ? o.isError : undefined,
    });
  }
  return out;
}

/**
 * Parse content_blocks_json from the DB row. Returns `null` when
 * the column is missing/empty so the caller can fall back to the
 * pre-migration synthesis. Drops malformed entries entry by entry
 * rather than failing the whole row — same pattern as parseToolCalls.
 */
export function parseContentBlocks(json: string | null): ContentBlock[] | null {
  if (!json) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  const out: ContentBlock[] = [];
  for (const raw of parsed) {
    if (!raw || typeof raw !== 'object') continue;
    const o = raw as {
      type?: string;
      text?: unknown;
      toolCallId?: unknown;
      trigger?: unknown;
      preTokens?: unknown;
      postTokens?: unknown;
      fromModel?: unknown;
      toModel?: unknown;
    };
    if (o.type === 'text' && typeof o.text === 'string') {
      out.push({ type: 'text', text: o.text });
    } else if (o.type === 'tool_use' && typeof o.toolCallId === 'string') {
      out.push({ type: 'tool_use', toolCallId: o.toolCallId });
    } else if (o.type === 'compact' && (o.trigger === 'auto' || o.trigger === 'manual')) {
      out.push({
        type: 'compact',
        trigger: o.trigger,
        ...(typeof o.preTokens === 'number' && Number.isFinite(o.preTokens) && o.preTokens >= 0
          ? { preTokens: o.preTokens }
          : {}),
        ...(typeof o.postTokens === 'number' && Number.isFinite(o.postTokens) && o.postTokens >= 0
          ? { postTokens: o.postTokens }
          : {}),
      });
    } else if (
      o.type === 'model_fallback' &&
      typeof o.fromModel === 'string' &&
      typeof o.toModel === 'string'
    ) {
      out.push({ type: 'model_fallback', fromModel: o.fromModel, toModel: o.toModel });
    }
  }
  return out;
}
